import React, { useEffect } from 'react';
import { SiteHeader } from './components/SiteHeader';
import { Hero } from './components/Hero';
import { ProofStrip } from './components/ProofStrip';
import { ServicesSection } from './components/ServicesSection';
import { ProjectsSection } from './components/ProjectsSection';
import { AboutSection } from './components/AboutSection';
import { CredentialsSection } from './components/CredentialsSection';
import { ServiceAreaSection } from './components/ServiceAreaSection';
import { ContactSection } from './components/ContactSection';
import { AnswersSection } from './components/AnswersSection';
import { SiteFooter } from './components/SiteFooter';
import { useDocumentMeta } from './hooks/useDocumentMeta';
import { AwardsPage } from './pages/AwardsPage';
import { GalleryPage } from './pages/GalleryPage';
import { ResidentialLandscapesPage } from './pages/ResidentialLandscapesPage';
import { residentialPage } from './data/residential';
import { LandDevelopmentPage } from './pages/LandDevelopmentPage';
import { landDevelopmentPage } from './data/landDevelopment';
import { CommercialLandscapeCarePage } from './pages/CommercialLandscapeCarePage';
import { commercialCarePage } from './data/commercialCare';
import { CommercialSeasonalServicesPage } from './pages/CommercialSeasonalServicesPage';
import { commercialSeasonalPage } from './data/commercialSeasonal';
import { AboutPage } from './pages/AboutPage';
import { aboutPage } from './data/about';
import { ContactPage } from './pages/ContactPage';
import { galleryPage } from './data/gallery';
import { awardsPageCopy } from './data/awardsPage';
import { ResourcesPage } from './pages/ResourcesPage';
import { resourcesPage } from './data/resources';
import { ServicesPage } from './pages/ServicesPage';
import { servicesPage } from './data/servicesPage';
import type { PageKey } from './data/site';
function getPage(): PageKey {
  if (typeof window === 'undefined') return 'home';
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  if (path === '/awards') return 'awards';
  if (path === '/gallery') return 'gallery';
  if (path === '/residential-landscapes') return 'residential';
  if (path === '/land-development') return 'landDevelopment';
  if (path === '/commercial-landscape-care') return 'commercialCare';
  if (path === '/commercial-seasonal-services') return 'commercialSeasonal';
  if (path === '/about') return 'about';
  if (path === '/contact') return 'contact';
  if (path === '/resources') return 'resources';
  if (path === '/services') return 'services';
  return 'home';
}
export function App() {
  const page = getPage();
  useDocumentMeta(page);
  useEffect(() => {
    let cancelled = false;
    let frame = 0;
    const scrollToFragment = () => {
      const hash = window.location.hash.slice(1);
      if (!hash) return;
      let id: string;
      try {
        id = decodeURIComponent(hash);
      } catch {
        return;
      }
      frame = window.requestAnimationFrame(() => {
        if (cancelled) return;
        document.getElementById(id)?.scrollIntoView({
          block: 'start',
          behavior: 'instant'
        });
      });
    };
    document.fonts.ready.then(() => {
      if (!cancelled) scrollToFragment();
    });
    window.addEventListener('hashchange', scrollToFragment);
    return () => {
      cancelled = true;
      window.cancelAnimationFrame(frame);
      window.removeEventListener('hashchange', scrollToFragment);
    };
  }, [page]);
  return <div className="min-h-screen w-full overflow-x-clip bg-ivory font-sans text-charcoal" data-id="element-0" data-mp="1r2682ckfahbg">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[70] focus:bg-ivory focus:px-4 focus:py-2 focus:text-olive" data-id="element-1" data-mp="1w2khnz1o9zbl">
        Skip to content
      </a>
      <SiteHeader data-id="element-2" data-mp="1m70lrcn3z0mu" />
      <main id="main" data-id="element-3" data-mp="6ontuoo9heuc">
        {page === 'home' && <>
            <Hero data-id="element-4" data-mp="6qs4rqkkve44" />
            <ProofStrip data-id="element-5" data-mp="apr7d4iu6yse" />
            <ServicesSection data-id="element-6" data-mp="aq992go9q57p" />
            <ProjectsSection data-id="element-7" data-mp="arc1wu55ihq9" />
            <AboutSection data-id="element-8" data-mp="qx5f5v2uoi8w" />
            <CredentialsSection data-id="element-9" data-mp="qxf73o7ggzh8" />
            <ServiceAreaSection data-id="element-10" data-mp="qyqa3lt2u37a" />
          </>}
        {page === 'awards' && <AwardsPage data-id="element-11" data-mp="m5dyqdderswh" />}
        {page === 'gallery' && <GalleryPage data-id="element-12" data-mp="m4m3x4rmxz79" />}
        {page === 'residential' && <ResidentialLandscapesPage data-id="element-13" data-mp="h8drw08qim4b" />}
        {page === 'landDevelopment' && <LandDevelopmentPage data-id="element-14" data-mp="c8j445k9acnh" />}
        {page === 'commercialCare' && <CommercialLandscapeCarePage data-id="element-15" data-mp="ca2ifqmcqdmm" />}
        {page === 'commercialSeasonal' && <CommercialSeasonalServicesPage data-id="element-16" data-mp="19uot9d460r26" />}
        {page === 'about' && <AboutPage data-id="element-17" data-mp="14x6iriai23s2" />}
        {page === 'contact' && <ContactPage data-id="element-18" data-mp="14xg9j83dceyt" />}
        {page === 'resources' && <ResourcesPage data-id="element-19" data-mp="zxk372778bg3" />}
        {page === 'services' && <ServicesPage data-id="element-20" data-mp="zw91r9187fs5" />}
        <AnswersSection page={page} data-id="element-21" data-mp="zzohme2q4sum" />
        {page === 'contact' ? null : page === 'services' ? <ContactSection eyebrow={null} title={servicesPage.closing.title} intro={servicesPage.closing.text} action={servicesPage.closing.action} showForm={false} data-id="element-22" data-mp="vyl1dqgrkusv" /> : page === 'residential' ? <ContactSection eyebrow={null} showForm={false} title={residentialPage.closing.title} intro={residentialPage.closing.text} action={{
        ...residentialPage.closing.action,
        href: '#service-inquiry'
      }} location={residentialPage.closing.location} data-id="element-23" data-mp="1w5zuc34l8cg6" /> : page === 'landDevelopment' ? <ContactSection eyebrow={null} showForm={false} title={landDevelopmentPage.closing.title} intro={landDevelopmentPage.closing.text} action={{
        ...landDevelopmentPage.closing.action,
        href: '#service-inquiry'
      }} data-id="element-24" data-mp="2etv6jikjxi76" /> : page === 'commercialCare' ? <ContactSection eyebrow={null} showForm={false} title={commercialCarePage.closing.title} intro={commercialCarePage.closing.text} action={{
        ...commercialCarePage.closing.action,
        href: '#service-inquiry'
      }} location={commercialCarePage.closing.location} data-id="element-25" data-mp="2stwefhz8hpw5" /> : page === 'commercialSeasonal' ? <ContactSection eyebrow={null} showForm={false} title={commercialSeasonalPage.closing.title} intro={commercialSeasonalPage.closing.text} action={{
        ...commercialSeasonalPage.closing.action,
        href: '#service-inquiry'
      }} location={commercialSeasonalPage.closing.location} data-id="element-26" data-mp="1wubwvlfm9w6t" /> : page === 'resources' ? <ContactSection eyebrow={null} title={resourcesPage.closing.title} intro={resourcesPage.closing.text} action={resourcesPage.closing.action} secondaryAction={resourcesPage.closing.secondaryAction} data-id="element-27" data-mp="2fr3c9js1fr5l" /> : page === 'awards' ? <ContactSection eyebrow={null} title={awardsPageCopy.closing.title} intro={awardsPageCopy.closing.text} action={awardsPageCopy.closing.action} secondaryAction={awardsPageCopy.closing.secondaryAction} data-id="element-28" data-mp="33etmrnu3woro" /> : page === 'gallery' ? <ContactSection eyebrow={null} title={galleryPage.closing.title} intro={galleryPage.closing.text} action={galleryPage.closing.action} secondaryAction={galleryPage.closing.secondaryAction} data-id="element-29" data-mp="385gxmgmcc765" /> : page === 'about' ? <ContactSection eyebrow={null} title={aboutPage.closing.title} intro={aboutPage.closing.text} action={aboutPage.closing.action} data-id="element-30" data-mp="343vc66vqqkkc" /> : <ContactSection data-id="element-31" data-mp="2uditylbef3hd" />}
      </main>
      <SiteFooter data-id="element-32" data-mp="2uebs4xwakknw" />
    </div>;
}